import "./FeedbackAnalysis.css";
import ContentPage from "./ContentPage";
import { ReactComponent as FeedbackAnalysisGraph } from "./static/images/FeedbackAnalysisGraph.svg";
import { ReactComponent as RightArrow } from "./static/icons/RightArrow.svg";
import Button from "./Button";
import { goToPage } from "./Slide";
import Highlighter from "./Highlighter";
import AnimatedDiv from "./AnimatedDiv";

function FeedbackAnalysis() {
  return (
    <ContentPage>
      <h2>User feedback analysis</h2>

      <h3>Role</h3>
      <p>UX Researcher | UX Writer | Product Designer </p>

      <h3>First things first. Meet Uproad.</h3>
      <p>
        The app that allows people to use toll roads, skip cash lanes and
        conveniently pay tolls as they go. Also, users'll get informative toll
        price alerts and smartly plan their route with trip cost estimates.{" "}
      </p>
      <p>
        The market is specific and complex and only the government operates in
        it. There is already an application provided by the government that
        allows users to pay tolls. But it lacks in usability, has bad design and
        overall UX is far from great; Unclear UX copy, the app works every
        second time, inaccessible design, and no customer support are just the
        tip of the iceberg of problems.
      </p>
      <p>
        All of these problems provided the best point of entry that Uproad took
        advantage of. Uproad wanted to do thins differently, in a new way and
        try to win over the market... The rest is already history.
      </p>

      <h3>Project overview</h3>
      <p>
        Creating good user experience is easier when you rely on data. Doing a
        user feedback research is the best way to identify the biggest pain
        points of your users.
      </p>
      <p>
        To develop a strategy of UX improvements, I've requested an export of
        customer service chats and created an extensive report for Product,
        Design and Growth teams.
      </p>

      <h3>Challenge</h3>
      <p>
        The exported file included thousands of conversations. To perform a
        proper semantic analysis, I had to find a pattern of most popular
        requests and create a list of tags to arrange data conveniently.
      </p>

      <h3>Process</h3>
      <p>
        I've used Microsoft Excel functions like{" "}
        <a
          href="https://support.microsoft.com/en-us/office/vlookup-function-0bbc8083-26fe-4963-8ab8-93a18ad188a1"
          target="_blank"
          rel="noopener noreferrer"
        >
          VLOOKUP
        </a>
        ,{" "}
        <a
          href="https://support.microsoft.com/en-us/office/concatenate-function-8f8ae884-2ca8-4f7a-b093-75d702bea31d"
          target="_blank"
          rel="noopener noreferrer"
        >
          CONCATENATE
        </a>
        , and conditional formatting to arrange data for my report.
      </p>

      {/*THE CASE STATS SECTION*/}
      <AnimatedDiv className="CaseStats">
        <div className="Stat">
          <div className="BigNumber">6852</div>
          <div className="StatDescription">
            Conversations were exported from Helpshift
          </div>
        </div>
        <div className="Stat">
          <div className="BigNumber">4130</div>
          <div className="StatDescription">
            Unique issues after duplicates were cleared
          </div>
        </div>
        <div className="Stat">
          <div className="BigNumber">6</div>
          <div className="StatDescription">
            Most popular requests were determined
          </div>
        </div>
      </AnimatedDiv>

      <AnimatedDiv>
        <p>
          Using this data, I've created a User Radar chart to visualise users'
          pet peeves.
        </p>
        <FeedbackAnalysisGraph />
      </AnimatedDiv>
      <p>This research helped to identify crucial user experience flaws:</p>

      <p>
        1. Uproad requires drivers to keep a prepaid balance. To make sure users
        always have a positive balance, they can enable the 'Autopay' feature.
        Whenever their balance falls below $10, Uproad charges the primary
        payment method automatically to replenish the balance.
      </p>
      <p>
        According to this research, users though that 'Autopay' meant automatic
        toll payments, so they've always enabled it to avoid late payments.
      </p>
      <p>
        2. It was not obvious to users that their toll payments will be
        processed within 1-30 days, depending on the tolling agency.
      </p>
      <p>
        3. Drivers tried to figure out how to pay for outstanding tolls, but
        Uproad can only do that in one state.
      </p>
      <p>
        4. 35% of users were seeking support after hours. Unfortunately, hiring
        new agents was not an option back then. This resulted in many annoyed
        users and bad store reviews.
      </p>

      <h3>Result</h3>
      <p>
        1. We renamed the
        <Highlighter text="'Autopay' feature to 'Auto-refill'." />
        Added several FAQ articles on how auto-refill works and additional
        explanation in the Wallet section of the app.
      </p>
      <p>
        As a result, we noticed a huge decrease in customer service requests
        regarding this issue.
      </p>
      <p>
        2. To communicate payment delays properly, we introduced
        <Highlighter text="new onboarding" />
        flow and informational blocks on the transaction details screens.
      </p>

      <Button
        className="goToCaseButton"
        variant="complex"
        text="SEE THE CASE"
        onClick={() => goToPage("/case3")}
        icon={<RightArrow />}
      />

      <p>
        3. As a part of new onboarding, we've proactively communicated that
        users can only pay for outstanding tolls in California.
      </p>
      <p>
        4. We developed a<Highlighter text="chatbot" />
        to automate the most popular requests.
      </p>
      <Button
        className="goToCaseButton"
        variant="complex"
        text="SEE THE CASE"
        onClick={() => goToPage("/case3.2.")}
        icon={<RightArrow />}
      />
    </ContentPage>
  );
}

export default FeedbackAnalysis;
